import {
  Component,
  ElementRef,
  Input,
  OnDestroy,
  OnInit,
  OnChanges,
  SimpleChanges,
  ChangeDetectorRef,
  ViewChild,
} from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { ApiService } from 'src/app/services/api/api.service';
import { SignupService } from 'src/app/services/signup/signup.service';
import { DomSanitizer } from '@angular/platform-browser';
import Utils from 'src/app/helpers/utils';
import { AuthService } from 'src/app/services/auth/auth.service';
import { ApprovalService } from 'src/app/services/approval/approval.service';

@Component({
  selector: 'app-dashboard-approval-signatures',
  templateUrl: './dashboard-approval-signatures.component.html',
  styleUrls: ['./dashboard-approval-signatures.component.scss'],
})
export class DashboardApprovalSignaturesComponent
  implements OnInit, OnDestroy, OnChanges
{
  @ViewChild('modalRoot') modalRoot;
  @Input() comparingVersions: boolean = false;

  type_dict = {
    assignor: 'Cedente',
    representative: 'Representante',
    provider: 'Prestador de serviço',
  };

  // Dados para versão principal
  mainData: any[] = [];
  mainLoading = false;

  // Dados para versão comparando
  comparingData: any[] = [];
  comparingLoading = false;
  comparingDataLoading = false;

  // Dados normais (sem comparação)
  data: any = [];
  loading = true;

  eventMainVersion: any;
  eventComparingVersion: any;

  file: any = null;

  isPDF = false;
  url: any;
  isXLS = false;

  constructor(
    private signupService: SignupService,
    private router: Router,
    private api: ApiService,
    private sanitizer: DomSanitizer,
    private route: ActivatedRoute,
    private authService: AuthService,
    private el: ElementRef,
    public approvalService: ApprovalService,
    private cdr: ChangeDetectorRef,
  ) {}

  ngOnInit(): void {
    if (this.comparingVersions) {
      this.loadComparingVersions();
    } else {
      if (this.approvalService.approvalData.signatures) {
        this.data = this.approvalService.approvalData.signatures;
        this.loading = false;
        setTimeout(() => {
          if (this.setHandlers) {
            this.setHandlers();
          }
        }, 100);
      } else {
        // Não chamar APIs v2 - sem dados no cache, não fazer loading
        this.data = [];
        this.loading = false;
      }
    }

    this.eventMainVersion = this.approvalService.changeMainVersion.subscribe(
      () => {
        if (this.comparingVersions) {
          this.loadMainVersion();
        }
      },
    );

    this.eventComparingVersion =
      this.approvalService.changeComparingVersion.subscribe(() => {
        if (this.comparingVersions) {
          this.loadComparingVersion();
        }
      });
  }

  ngOnDestroy() {
    if (this.eventMainVersion) {
      this.eventMainVersion.unsubscribe();
    }
    if (this.eventComparingVersion) {
      this.eventComparingVersion.unsubscribe();
    }
  }

  ngOnChanges(changes: SimpleChanges) {
    // Detectar quando comparingVersions muda de true para false
    if (
      changes['comparingVersions'] &&
      !changes['comparingVersions'].firstChange &&
      changes['comparingVersions'].previousValue === true &&
      changes['comparingVersions'].currentValue === false
    ) {
      // Recarregar dados para visualização normal
      if (this.approvalService.approvalData.signatures) {
        this.data = this.approvalService.approvalData.signatures;
        this.loading = false;
      } else {
        this.getApprovalData();
      }
    } else if (changes['approval'] && changes['approval'].currentValue) {
      this.data = changes['approval'].currentValue.signatures;
      setTimeout(() => {
        if (this.setHandlers) {
          this.setHandlers();
        }
      }, 1000);
    }
  }

  loadComparingVersions() {
    // Só carregar se houver versões selecionadas
    if (this.approvalService.approvalData.mainRevision) {
      this.loadMainVersion();
    }
    if (this.approvalService.approvalData.comparingRevision) {
      this.loadComparingVersion();
    }
  }

  loadMainVersion() {
    if (!this.approvalService.approvalData.mainRevision) {
      this.mainData = [];
      this.mainLoading = false;
      return;
    }
    this.mainLoading = true;
    this.cdr.detectChanges();
    this.getApprovalData('main');
  }

  loadComparingVersion() {
    if (!this.approvalService.approvalData.comparingRevision) {
      this.comparingData = [];
      this.comparingDataLoading = false;
      this.comparingLoading = false;
      return;
    }
    this.comparingDataLoading = true;
    this.comparingLoading = true;
    this.cdr.detectChanges();
    this.getApprovalData('comparing');
  }

  // Função para comparar os dados e aplicar classes de comparação
  async compareData() {
    try {
      this.comparingDataLoading = true;
      this.cdr.detectChanges();

      if (!this.mainData || !this.comparingData) {
        this.comparingDataLoading = false;
        this.comparingLoading = false;
        return;
      }

      const processedData: any[] = [];

      // Processar itens da tabela de comparação (direita)
      this.comparingData.forEach((comparingItem) => {
        const referenceItem = this.mainData.find(
          (ref) => ref.file === comparingItem.file,
        );

        if (referenceItem) {
          // Item existe em ambas as tabelas - verificar se há diferenças
          let hasChanges = false;
          const processedSigns: any[] = [];

          // Processar cada sign do comparing
          comparingItem.signs.forEach((comparingSign: any) => {
            const refSign = referenceItem.signs.find(
              (s: any) => s.id === comparingSign.id,
            );

            const signChangedFields: any = {};

            if (refSign) {
              // Sign existe em ambas - comparar campos
              if (refSign.approved !== comparingSign.approved) {
                signChangedFields.approved = true;
                hasChanges = true;
              }
              if (refSign.signed !== comparingSign.signed) {
                signChangedFields.signed = true;
                hasChanges = true;
              }
              if (refSign.updated_at !== comparingSign.updated_at) {
                signChangedFields.date = true;
                hasChanges = true;
              }
              if (refSign.type_display !== comparingSign.type_display) {
                signChangedFields.type = true;
                hasChanges = true;
              }

              // Comparar register
              const refIsPerson = refSign.register?.type === 'PF';
              const comparingIsPerson = comparingSign.register?.type === 'PF';

              if (refIsPerson !== comparingIsPerson) {
                signChangedFields.name = true;
                hasChanges = true;
              } else {
                if (refIsPerson) {
                  if (
                    refSign.register?.person?.full_name !==
                    comparingSign.register?.person?.full_name
                  ) {
                    signChangedFields.name = true;
                    hasChanges = true;
                  }
                } else {
                  if (
                    refSign.register?.company?.corporate_name !==
                    comparingSign.register?.company?.corporate_name
                  ) {
                    signChangedFields.name = true;
                    hasChanges = true;
                  }
                }
              }

              processedSigns.push({
                ...comparingSign,
                changedFields: signChangedFields,
              });
            } else {
              // Sign novo (existe só na direita)
              hasChanges = true;
              processedSigns.push({
                ...comparingSign,
                changedFields: {},
              });
            }
          });

          // Verificar se há signs removidos (existem só na esquerda)
          referenceItem.signs.forEach((refSign: any) => {
            const existsInComparing = comparingItem.signs.find(
              (s: any) => s.id === refSign.id,
            );
            if (!existsInComparing) {
              hasChanges = true;
              processedSigns.push({
                ...refSign,
                changedFields: {},
              });
            }
          });

          processedData.push({
            ...comparingItem,
            signs: processedSigns,
            compareClass: hasChanges ? 'row-compare-modified' : '',
          });
        } else {
          // Item novo (existe só na direita)
          processedData.push({
            ...comparingItem,
            compareClass: 'row-compare-added',
          });
        }
      });

      // Adicionar itens removidos (existem só na esquerda)
      this.mainData.forEach((referenceItem) => {
        const existsInComparing = this.comparingData.find(
          (comp) => comp.file === referenceItem.file,
        );

        if (!existsInComparing) {
          // Item removido - adicionar na lista de comparação com classe removed
          processedData.push({
            ...referenceItem,
            compareClass: 'row-compare-removed',
          });
        }
      });

      this.comparingData = processedData;
      this.comparingDataLoading = false;
      this.comparingLoading = false;
      this.cdr.detectChanges();
      setTimeout(() => {
        if (this.setHandlers) {
          this.setHandlers();
        }
      }, 100);
    } catch (error) {
      this.comparingDataLoading = false;
      this.comparingLoading = false;
    }
  }

  async getApprovalData(versionType: 'main' | 'comparing' | null = null) {
    // Não chamar APIs v2 - apenas verificar cache
    // Se não houver dados, não fazer nada e não mostrar loading
    if (versionType === 'main') {
      this.mainData = [];
      this.mainLoading = false;
      this.cdr.detectChanges();
    } else if (versionType === 'comparing') {
      this.comparingData = [];
      this.comparingLoading = false;
      this.comparingDataLoading = false;
      this.cdr.detectChanges();
    } else {
      this.data = [];
      this.loading = false;
    }
    return;

    /* Código antigo comentado - não chamar APIs v2
    try {
      const req_params: any = {
        register: this.route.snapshot.params.id,
        tab: 'signatures',
      };

      if (
        versionType === 'main' &&
        this.approvalService.approvalData.mainVersion
      ) {
        req_params['version'] = this.approvalService.approvalData.mainVersion;
      } else if (
        versionType === 'comparing' &&
        this.approvalService.approvalData.comparingVersion
      ) {
        req_params['version'] =
          this.approvalService.approvalData.comparingVersion;
      } else if (!versionType && this.approvalService.approvalData.version) {
        req_params['version'] = this.approvalService.approvalData.version;
      }

      // Se estiver em modo de comparação e não houver versão selecionada, não fazer requisição
      if (
        this.comparingVersions &&
        versionType &&
        ((versionType === 'main' &&
          !this.approvalService.approvalData.mainRevision) ||
          (versionType === 'comparing' &&
            !this.approvalService.approvalData.comparingRevision))
      ) {
        if (versionType === 'main') {
          this.mainData = [];
          this.mainLoading = false;
          this.cdr.detectChanges();
        } else if (versionType === 'comparing') {
          this.comparingData = [];
          this.comparingLoading = false;
          this.comparingDataLoading = false;
          this.cdr.detectChanges();
        }
        return;
      }
      const data = await this.api.get<UserApprovalResponse[]>({
        route: `api/v2/approvals/version/`,
        token: true,
        params: req_params,
      });

      if (versionType === 'main') {
        this.mainData = data[0].register.signatures ?? [];
        this.mainLoading = false;
        // Se já temos comparing data, fazer comparação
        if (this.comparingVersions && this.comparingData) {
          await this.compareData();
        }
        this.cdr.detectChanges();
      } else if (versionType === 'comparing') {
        this.comparingData = data[0].register.signatures ?? [];
        this.comparingLoading = false;
        this.comparingDataLoading = false;
        // Se já temos main data, fazer comparação
        if (this.comparingVersions && this.mainData) {
          await this.compareData();
        }
        this.cdr.detectChanges();
      } else {
        this.approvalService.approvalData.signatures =
          data[0].register.signatures;
        this.data = data[0].register.signatures ?? [];
        this.loading = false;
        setTimeout(() => {
          this.setHandlers();
        }, 100);
      }
    } catch (error) {
      if (versionType === 'main') {
        this.mainLoading = false;
        this.cdr.detectChanges();
      } else if (versionType === 'comparing') {
        this.comparingLoading = false;
        this.comparingDataLoading = false;
        this.cdr.detectChanges();
      } else {
        this.loading = false;
      }
    }
    */
  }

  // Funções auxiliares para exibir dados na tabela
  getName(sign: any): string {
    const is_person = sign.register?.type === 'PF';
    const person = is_person ? sign.register?.person : sign.register?.company;
    return is_person ? person?.full_name || '' : person?.corporate_name || '';
  }

  getInitialsFromSign(sign: any): string {
    const name = this.getName(sign);
    return this.getInitials(name);
  }

  getSignedIcon(sign: any): string {
    if (sign.signed) {
      if (sign.approved) {
        return 'assets/images/icon-check-filled.svg'; // Filled check icon
      }
      return 'assets/images/icon-times-circle.svg';
    }
    return 'assets/images/icon-warning.svg';
  }

  getSignedText(sign: any): string {
    return sign.signed ? 'Visualizar documento' : 'Aguardando assinatura';
  }

  isFieldChanged(sign: any, field: string): boolean {
    if (!sign || !sign.changedFields) return false;
    return sign.changedFields[field] === true;
  }

  setHandlers() {
    setTimeout(() => {
      const visualizer = document.querySelectorAll('.document-visualizer');

      visualizer.forEach((button) => {
        // Remover listeners anteriores se existir método
        if ((button as any).removeAllListeners) {
          (button as any).removeAllListeners();
        }

        // Remover listener anterior antes de adicionar novo
        const newButton = button.cloneNode(true);
        button.parentNode?.replaceChild(newButton, button);

        newButton.addEventListener('click', (event) => {
          const el: any = event.currentTarget;
          const idClass: any = el.classList.item(1);
          const id = idClass.split('-')[1];

          // Buscar em todos os dados (main, comparing ou normal)
          let allSigns: any[] = [];

          if (this.comparingVersions) {
            this.mainData.forEach((item: any) => {
              if (item.signs) allSigns = [...allSigns, ...item.signs];
            });
            this.comparingData.forEach((item: any) => {
              if (item.signs) allSigns = [...allSigns, ...item.signs];
            });
          } else {
            this.data.forEach((item: any) => {
              if (item.signs) allSigns = [...allSigns, ...item.signs];
            });
          }

          const finded = allSigns.find((sign: any) => {
            if (sign.id) {
              return Number(sign.id) === Number(id);
            }
            return false;
          });

          if (finded) {
            this.openFile(finded);
          }
        });
      });
    }, 100);
  }

  openFile(attachment: any) {
    this.file = attachment;
    this.getImgUrl();
  }

  redirectUser(item: any) {
    this.router.navigateByUrl(
      'lorem',
      // `/app/details/${this.users[value].applicable}/${this.users[value].id}`
    );
  }

  openDocumentInNewTab(url: string) {
    window.open(`${url}`, '_blank');
  }

  getInitials(name: string) {
    if (name === undefined) {
      return '';
    }

    return Utils.getInitials(name);
  }

  getLinkName(value: number) {
    const link = this.signupService
      .choices()
      .representative_link.find((item) => item.value == value.toString());

    return link?.label;
  }

  formatDate(date: string) {
    const day = date.split('T').shift()?.split('-').reverse().join('/');
    const hours = date.split('T').pop()?.slice(0, 5);

    return `${day} às ${hours}`;
  }

  getImgUrl() {
    const regex = /\.([0-9a-z]+)(?:[\?#]|$)/i;

    const resultado = regex.exec(this.file.file);

    if (resultado && resultado[1]) {
      const extension = resultado[1].toLowerCase();

      switch (extension) {
        case 'pdf':
          this.isPDF = true;
          break;
        case 'xls':
        case 'xlsx':
          this.isXLS = true;
          break;
        default:
          this.isPDF = false;
          this.isXLS = false;
          break;
      }
    }

    let url: any = this.file.file;

    if (this.isPDF) {
      url = this.sanitizer.bypassSecurityTrustResourceUrl(this.file.file);
    }

    if (this.isXLS) {
      url = this.sanitizer.bypassSecurityTrustResourceUrl(
        `https://view.officeapps.live.com/op/embed.aspx?src=${this.file.file}`,
      );
    }

    this.url = url;

    this.modalRoot.show();
  }
}
